import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import React from 'react';
import * as z from 'zod';
import { useLocalization } from '@ais-ui/locale-management';
import { useSettings } from '../SettingsProvider';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Bookmark, useSaveSettings } from '@ais-ui/websockets';

const homeBookmarkFormSchema = z.object({
  bookmarkId: z.string().min(1),
});

type HomeBookmarkFormValues = z.infer<typeof homeBookmarkFormSchema>;

interface HomeBookmarkProps {
  bookmarks: Bookmark[];
}

export const HomeBookmark = ({ bookmarks }: HomeBookmarkProps) => {
  const { messages } = useLocalization();
  const { toast } = useToast();

  const { settings } = useSettings();
  const { mutate: mutateSettings } = useSaveSettings();
  const form = useForm<HomeBookmarkFormValues>({
    resolver: zodResolver(homeBookmarkFormSchema),
    defaultValues: {
      bookmarkId: settings?.homeBookmark?.uuid || '',
    },
  });

  const onSubmit = async (data: HomeBookmarkFormValues) => {
    const settingsToSave = {
      ...settings,
      homeBookmark: bookmarks.find((b) => b.uuid === data.bookmarkId),
    };

    // Save the updated settings
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          title: messages.genericToastTitleSuccess,
          description: messages.settingsBookmarksSetHomeSuccessAddMessage,
        });
      },
      onError: () => {
        toast({
          title: messages.genericToastTitleError,
          description: messages.settingsBookmarksSetHomeFailedAddMessage,
          variant: 'destructive',
        });
      },
    });
  };

  return (
    <Form {...form}>
      <FormField
        key="bookmarkId"
        control={form.control}
        name="bookmarkId"
        render={({ field }) => (
          <FormItem
            className={cn(
              'flex flex-row items-center justify-between border rounded-lg p-4'
            )}
          >
            <div className="space-y-0.5">
              <FormLabel className="text-base">
                {messages.settingsBookmarksHomeLabel}
              </FormLabel>
              <FormDescription>
                {messages.settingsBookmarksHomeDescription}
              </FormDescription>
            </div>
            <FormControl>
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  form.handleSubmit(onSubmit)();
                }}
              >
                <SelectTrigger className="w-[240px]">
                  <SelectValue placeholder="Select a home bookmark" />
                </SelectTrigger>
                <SelectContent>
                  {bookmarks.map((bookmark) => (
                    <SelectItem key={bookmark.uuid} value={bookmark.uuid}>
                      {bookmark.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
          </FormItem>
        )}
      />
    </Form>
  );
};
